import React, { useState } from 'react';
import { Heart, ChevronDown, ChevronUp, Shield, Clock, Users, Settings, HelpCircle } from 'lucide-react';
import { useStaggeredAnimation } from '../hooks/useScrollAnimation';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: 'setup' | 'privacy' | 'support' | 'operation';
  icon: React.ReactNode;
}

interface FAQSectionProps {
  isVisible?: boolean;
}

const FAQSection: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  // FAQ段階アニメーション
  const faqAnimation = useStaggeredAnimation(5, {
    threshold: 0.1,
    delay: 150,
    triggerOnce: true
  });

  const faqItems: FAQItem[] = [
    {
      id: 1,
      category: 'setup',
      icon: <Settings size={20} />,
      question: '導入までの流れを教えてください',
      answer: '導入は3ステップで完了します。\n①お問い合わせ（式場様との詳細な要件確認とカスタマイズ相談）\n②アカウント作成（専用QRコードを作成し、席札やウェルカムボード用データをご提供）\n③QRコード作成・スクリーン準備（QRコード・スクリーンの準備ができます）。'
    },
    {
      id: 2,
      category: 'privacy',
      icon: <Shield size={20} />,
      question: '参列者のプライバシーは大丈夫ですか？',
      answer: 'プライバシー保護を最優先に設計されており、以下の対策を実施しています。\n①モデレーション機能（式場スタッフによる事前承認システム）\n②データ暗号化（アップロード時・保存時の高度暗号化）\n③アクセス制限（QRコード認証による限定アクセス）\n④自動削除機能（式終了後30日で自動データ削除）。GDPR準拠の厳格なデータ管理体制を整えています。'
    },
    {
      id: 3,
      category: 'operation',
      icon: <Users size={20} />,
      question: '年配のゲストでも使えますか？',
      answer: '年配の方にも優しい設計となっております。\n①直感的UI（大きなボタン・分かりやすいアイコン）\n②スマートフォン不要（QRコード読み取り後は簡単タップ操作のみ）\n③現場サポート（スタッフが操作方法をご案内）。'
    },
    {
      id: 4,
      category: 'setup',
      icon: <HelpCircle size={20} />,
      question: '費用はどのくらいかかりますか？',
      answer: '月額プラン：5万円（人数無制限／会場規模不問）会場規模・利用人数にかかわらず、一律で月額5万円です。'
    },
    {
      id: 5,
      category: 'operation',
      icon: <Settings size={20} />,
      question: '会場の設備要件はありますか？',
      answer: '最小限の設備で導入可能です。\n①インターネット接続（Wi-Fi推奨、4G/5G回線でも対応可）\n②プロジェクター・スクリーン（写真投影用、多くの式場で標準装備）\n③電源（ノートPC用、通常のコンセントで十分）。特別な機材の購入は不要で、弊社が必要な技術機器をすべてご用意いたします。設備チェックリストをお渡しし、事前確認も行います。'
    }
  ];

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const getCategoryName = (category: string) => {
    const categories = {
      setup: '導入・設定',
      privacy: 'プライバシー・セキュリティ',
      support: 'サポート・対応',
      operation: '運用・操作'
    };
    return categories[category as keyof typeof categories];
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      setup: '#c7aa7a',
      privacy: '#8b7d6b',
      support: '#b29861',
      operation: '#d8c3a5'
    };
    return colors[category as keyof typeof colors];
  };

  return (
    <section className="faq-section">
      <div className="section-header">
        <div className="title-decoration">
          <div className="decoration-line"></div>
          <Heart className="section-heart" />
          <div className="decoration-line"></div>
        </div>
        <p className="faq-subtitle">
          Photo Memorial の導入をご検討中の式場様からよくいただくご質問にお答えします
        </p>
      </div>

      <div className="faq-container">

        <div className="faq-list" ref={faqAnimation.ref as React.RefObject<HTMLDivElement>}>
          {faqItems.map((item, index) => (
            <div key={item.id} className={`faq-item ${faqAnimation.visibleItems[index] ? 'visible' : ''}`}>
              <div 
                className="faq-question"
                onClick={() => toggleItem(item.id)}
              >
                <div className="question-content">
                  <div className="question-icon" style={{ color: getCategoryColor(item.category) }}>
                    {item.icon}
                  </div>
                  <div className="question-text">
                    <div className="question-category">
                      {getCategoryName(item.category)}
                    </div>
                    <h3>{item.question}</h3>
                  </div>
                </div>
                <div className="question-toggle">
                  {openItems.includes(item.id) ? (
                    <ChevronUp size={24} />
                  ) : (
                    <ChevronDown size={24} />
                  )}
                </div>
              </div>
              
              {openItems.includes(item.id) && (
                <div className="faq-answer">
                  <div className="answer-content">
                    <p>{item.answer}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* <div className="faq-footer">
          <div className="faq-cta">
            <h3>その他のご質問がございましたら</h3>
            <p>
              お気軽にお問い合わせください。経験豊富なスタッフが、
              御式場に最適なプランをご提案いたします。
            </p>
            <div className="faq-contact-info">
              <div className="contact-method">
                <strong>📞 お電話でのお問い合わせ</strong>
                <p>0120-123-456（平日9:00-18:00）</p>
              </div>
              <div className="contact-method">
                <strong>✉️ メールでのお問い合わせ</strong>
                <p>info@photo-memorial.com（24時間受付）</p>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default FAQSection;